'use client'

import { BottomSheet, Button, Cell, Dialog, Image, Navigation, Spacer, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  IvsGuidelineBannerInformationModel,
  IvsGuidelineBannerSectionModel,
  clickEvent,
} from '@marketplace-web/banners/banners-data'

import { ITEM_UPLOAD_URL } from '../../../constants/routes'

type Props = {
  isOpen: boolean
  onClose: () => void
  info: IvsGuidelineBannerInformationModel
}

const InfoDialog = ({ isOpen, onClose, info }: Props) => {
  const translate = useTranslate('ivs_guideline_banner')
  const breakpoint = useBreakpoint()
  const { track } = useTracking()

  const handleClose = () => {
    track(
      clickEvent({
        target: 'ivs_guideline_banner',
        targetDetails: 'close_ivs_how_to',
      }),
    )

    onClose()
  }

  const renderNavigation = () => {
    return (
      <Navigation
        right={
          <Button
            inline
            iconName={X24}
            styling="flat"
            onClick={handleClose}
            testId="info-dialog-close-button"
            aria={{ 'aria-label': translate('a11y.actions.close') }}
          />
        }
      />
    )
  }

  const renderTitle = () => {
    return (
      <div className="u-ui-padding-large u-ui-padding-right-x4-large">
        <Text type="heading" as="p">
          {info.title}
        </Text>
      </div>
    )
  }

  const renderSection = ({ imageUrl, title, body }: IvsGuidelineBannerSectionModel) => {
    return (
      <div key={title} className="u-flexbox">
        <div>
          <Image src={imageUrl} size="x-large" />
        </div>
        <Spacer orientation="vertical" size="x-large" />
        <div>
          <Text type="title" as="p">
            {title}
          </Text>
          <Spacer size="regular" />
          <Text type="subtitle" as="p" theme="amplified">
            {body}
          </Text>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    return (
      <div>
        {renderTitle()}
        <Spacer size="x-large" />
        <div className="u-ui-padding-horizontal-large">
          <SeparatedList separator={<div className="ivs-guideline-dialog__content-separator" />}>
            {info.sections.map(renderSection)}
          </SeparatedList>
        </div>
        <Spacer size="x2-large" />
      </div>
    )
  }

  const handlePrimaryClick = () => {
    track(
      clickEvent({
        target: 'ivs_guideline_banner',
        targetDetails: 'upload_item_ivs_how_to',
      }),
    )
  }

  const handleSecondaryClick = () => {
    track(
      clickEvent({
        target: 'ivs_guideline_banner',
        targetDetails: 'learn_more_ivs_how_to',
      }),
    )
  }

  const renderActions = () => {
    return (
      <Cell styling="default">
        <Button styling="filled" url={ITEM_UPLOAD_URL} onClick={handlePrimaryClick}>
          {info.actions.primary.title}
        </Button>
        <Spacer size="large" />
        <Button
          styling="flat"
          url={info.actions.secondary.url}
          onClick={handleSecondaryClick}
          urlProps={{ target: '_blank' }}
        >
          {info.actions.secondary.title}
        </Button>
      </Cell>
    )
  }

  if (!isOpen) return null

  if (breakpoint.phones) {
    return (
      <BottomSheet isVisible onClose={handleClose} initialHeight={1} closeOnOverlayClick>
        {renderNavigation()}
        {renderContent()}
        <div className="u-sticky u-bottom">{renderActions()}</div>
      </BottomSheet>
    )
  }

  return (
    <Dialog
      show
      defaultCallback={handleClose}
      closeOnOverlay
      contentDimensions={{ maxWidth: '343px' }}
    >
      {renderNavigation()}
      <div className="ivs-guideline-dialog__content">{renderContent()}</div>
      {renderActions()}
    </Dialog>
  )
}

export default InfoDialog
